import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * ReasoningTraceCard - Collapsed debug card showing per-step reasoning and extracted key facts.
 * Only appears when agent_config.debug_reasoning_trace = true.
 *
 * @param {string} step_summary - Reasoning text (LEARN block already stripped)
 * @param {string|null} learn - Key facts extracted from the tool response
 */
const ReasoningTraceCard = ({ step_summary, learn }) => {
  const [expanded, setExpanded] = useState(false);

  const summaryLines = (step_summary || "").split("\n").filter((l) => l.trim());
  const learnLines = (learn || "").split("\n").filter((l) => l.trim());

  // Use first "Completed step N" / "Next:" / "All steps complete" line as the collapsed label
  const statusLine = summaryLines.find((l) =>
    /completed step|next:|all steps complete/i.test(l)
  );
  const headerLabel = statusLine ? statusLine.replace(/^[-•*]\s*/, "").trim() : "Reasoning trace";

  return (
    <div className="border border-edge-amber-200 dark:border-edge-hex-78350f/50 bg-fill-hex-fffbeb dark:bg-fill-hex-451a03/25 rounded-[10px] overflow-hidden">
      <div
        className="flex items-center justify-between px-3 py-3 sm:px-[12px] sm:py-[14px] cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <p className="text-sm text-[14px] text-ink-amber-700 dark:text-ink-amber-400 truncate mr-2">
          {headerLabel}
        </p>
        <span className="text-ink-amber-500 dark:text-ink-amber-400 flex-shrink-0 ml-2">
          {expanded ? <ChevronUp size={14} strokeWidth={2.5} /> : <ChevronDown size={14} strokeWidth={2.5} />}
        </span>
      </div>

      {expanded && (
        <div className="bg-fill-hex-fef3c7/60 dark:bg-fill-hex-451a03/25 px-3 py-3 sm:px-[12px] space-y-3">
          {summaryLines.length > 0 && (
            <ul className="space-y-1 list-none m-0 p-0">
              {summaryLines.map((line, i) => (
                <li key={i} className="text-[13px] text-ink-amber-800 dark:text-ink-amber-300 leading-snug">
                  {line}
                </li>
              ))}
            </ul>
          )}

          {learnLines.length > 0 && (
            <div>
              <p className="text-[11px] uppercase font-semibold text-ink-hex-d97706 dark:text-ink-amber-500 mb-1 tracking-wide">
                Key facts extracted
              </p>
              <ul className="space-y-1 list-none m-0 p-0">
                {learnLines.map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-ink-amber-800 dark:text-ink-amber-300">
                    <span className="flex-shrink-0 text-ink-amber-400 dark:text-ink-amber-500 mt-[2px]">·</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReasoningTraceCard;
